import { yupResolver } from '@hookform/resolvers/yup';
import { useI18n } from '@i18n';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, MenuItem } from '@mui/material';
import { PortfolioManagementPortfolioFragment } from '@predium/client-graphql';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { FormProvider, RHFSelect, RHFTextField } from '@/components/hook-form';
import Label from '@/components/Label';
import PreDialog, { PreDialogBody } from '@/components/presentations/PreDialog/PreDialog';
import { CrremOrgPreferences } from '@/contexts/OrgPreferences/OrgPreferencesContext';
import usePosthogTracking from '@/hooks/usePosthogTracking';
import CRREMConfigToggle, { CRREMConfigToggleEnum } from '@/sections/Org/Portfolio/EmissionFactorToggle';
import { PortfolioFormValuesProps } from '@/sections/Org/Portfolio/PortfolioCreateDialog';

type Props = {
  open: boolean;
  onClose: () => void;
  onPortfolioCreate?: () => void;
  portfolios: PortfolioManagementPortfolioFragment[];
  loading: boolean;
  createMutateFunction: any;
  orgPreferences: CrremOrgPreferences;
};

export function CrremCreatePortfolioForm({
  open,
  onClose,
  onPortfolioCreate,
  portfolios,
  loading,
  createMutateFunction,
  orgPreferences,
}: Props) {
  const { t } = useI18n();
  const { trackEvent } = usePosthogTracking();

  const [currentEmissionFactorToggle, setCurrentEmissionFactorToggle] = useState(CRREMConfigToggleEnum.DEFAULT);

  const { crremConfigurations, activeCRREMConfiguration } = orgPreferences;

  const NewPortfolioSchema = object().shape({
    name: string()
      .required(t('General_PortfolioNameIsRequired'))
      .notOneOf(
        portfolios.map((p: any) => p.name),
        t('General_PortfolioAlreadyExists'),
      ),
    crremConfigId: string()
      .required(t('Portfolio_CRREMConfigRequired'))
      .oneOf(
        crremConfigurations.map((c: any) => c.getStringId()),
        t('Portfolio_CRREMConfigRequired'),
      ),
  });

  const defaultValues: PortfolioFormValuesProps = useMemo(
    () => ({
      name: '',
      crremConfigId: activeCRREMConfiguration ? activeCRREMConfiguration.getStringId() : '',
    }),
    [activeCRREMConfiguration],
  );

  const methods = useForm<PortfolioFormValuesProps>({
    resolver: yupResolver(NewPortfolioSchema),
    defaultValues,
    mode: 'onChange',
  });

  if (!activeCRREMConfiguration) {
    return null;
  }

  const { handleSubmit, reset, setValue } = methods;
  const crremConfigFieldName = 'crremConfigId';

  const onEmissionTypeToggle = (value: CRREMConfigToggleEnum) => {
    setCurrentEmissionFactorToggle(value);
    if (value === CRREMConfigToggleEnum.DEFAULT) {
      setValue(crremConfigFieldName, activeCRREMConfiguration ? activeCRREMConfiguration.getStringId() : '');
    } else {
      setValue(crremConfigFieldName, '');
    }
  };

  const onSubmit = async (data: PortfolioFormValuesProps) => {
    const chosenConfig =
      currentEmissionFactorToggle === CRREMConfigToggleEnum.DEFAULT
        ? undefined
        : crremConfigurations.find((config: any) => config.getStringId() === data.crremConfigId);

    const createResult = await createMutateFunction({
      variables: {
        name: data.name,
        baseEmissionFactorType: chosenConfig?.getBaseEmissionFactor(),
        configId: chosenConfig?.getConfigId(),
      },
    });

    if (createResult.errors?.length !== 0 || !createResult.errors) {
      trackEvent('PORTFOLIO_CREATED', {
        emissionFactorType:
          currentEmissionFactorToggle === CRREMConfigToggleEnum.DEFAULT ? null : (data.crremConfigId ?? null),
      });
    }
    onClose();
    reset();
    onPortfolioCreate?.();
  };

  return (
    <PreDialog open={open} onClose={onClose} fullWidth type="definedByChildren">
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <PreDialogBody
          dialogtitle={t('General_CreateNewPortfolio')}
          content={
            <Grid container>
              <PTypography variant="body1" align={'justify'} sx={{ mb: 3 }}>
                {t('PortfolioCreateDialog_Description')}
              </PTypography>
              <Grid item xs={12}>
                <RHFTextField name="name" label={t('General_EnterName')} />
              </Grid>
              <Grid container alignItems={'center'} mt={4} mb={2}>
                <Grid item xs={8}>
                  <PTypography variant="subtitle1">{t('OrgSettings_DatabaseEmissionFactors')}</PTypography>
                </Grid>
                <Grid item xs={4} mb={1}>
                  <CRREMConfigToggle
                    onEmissionTypeToggle={onEmissionTypeToggle}
                    defaultValue={currentEmissionFactorToggle}
                  />
                </Grid>
                <Grid item xs={12} mb={2}>
                  <RHFSelect
                    name={crremConfigFieldName}
                    label={t('General_SelectDatabaseEmissionFactors')}
                    state={currentEmissionFactorToggle === CRREMConfigToggleEnum.DEFAULT ? 'disabled' : 'enabled'}
                  >
                    {crremConfigurations.map((config: any) => (
                      <MenuItem
                        key={config.getStringId()}
                        value={config.getStringId()}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        {config.getTitleElement()}
                        {config.getStringId() === activeCRREMConfiguration.getStringId() ? (
                          <Label ml={1}> {t('General_Standard')} </Label>
                        ) : null}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>
              </Grid>
            </Grid>
          }
          actions={
            <>
              <Button variant="outlined" onClick={onClose}>
                {t('General_Cancel')}
              </Button>
              <LoadingButton type="submit" variant="contained" loading={loading}>
                {t('General_Create')}
              </LoadingButton>
            </>
          }
        ></PreDialogBody>
      </FormProvider>
    </PreDialog>
  );
}
